/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useLoader from "../../../../../hooks/useLoader";
import useNotification from "../../../../../hooks/useNotification";
import useBaseService from "../../../../../hooks/services/useBaseService";
import useForm from "../../../../../hooks/useForm";
import {
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../../../utils/objectUtils";
import { entities } from "../../../../../constants/entities";
import useRemoteSelectOptions from "../../../../../hooks/useRemoteSelectOptions";
import Form from "../../../../../components/forms/Form";
import MainLayout from "../../../../../layouts/MainLayout";
import PageHeader from "../../../../../components/PageHeader";
import SectionHeader from "../../../../../components/forms/SectionHeader";
import SimpleBody from "../../../../../components/forms/SimpleBody";
import { associeteGroupFields } from "../../../../../constants/forms/almoxarifado/groupFields";
import useQueryWithLoader from "../../../../../hooks/useQueryWithLoader";
import { Box, Chip } from "@mui/material";

const AttachGroupToTypePage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const {
    get: baseServiceGet,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);

  const { formData, handleChange, setFormData } = useForm(
    setDefaultFieldValues(associeteGroupFields),
    associeteGroupFields,
  );
  const isInitialLoad = useRef(true);

  // --- Query para carregar grupos associados ao tipo (apenas IDs e nomes) ---
  const {
    data: typeGroupsData,
    isLoading: isLoadingTypeGroups,
    isError: isErrorTypeGroups,
    error: typeGroupsError,
  } = useQueryWithLoader({
    queryKey: ["almox_typeGroups", id],
    queryFn: async () => {
      const response = await baseServiceGet(entities.types.typeGroups(id), {
        deleted_at: false,
      });
      return response.result;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });

  const typeSelect = useRemoteSelectOptions({
    queryKey: ["typeOptions"],
    entityPath: entities.types.get,
    baseServiceGet,
    extraFilters: { id: id },
    fieldId: "type_id",
    formData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (type) => ({ value: type.id, label: type.name }),
    immediateFetch: true,
  });

  // --- Selects ---
  const selects = {
    type_id: typeSelect,
  };

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      if (!selectConfig) return {};

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      };
    },
    [selects],
  );

  // Preenche o formulário com grupos vinculados na carga inicial
  useEffect(() => {
    if (
      isInitialLoad.current &&
      !isLoadingTypeGroups &&
      !isErrorTypeGroups &&
      typeGroupsData &&
      typeSelect.options.length > 0
    ) {
      setFormData({
        type_id: typeSelect.options.find((opt) => opt.value === parseInt(id)),
      });
      hideLoader();
      isInitialLoad.current = false;
    }

    if (isErrorTypeGroups || typeGroupsError) {
      console.error("Erro ao carregar grupos do tipo:", typeGroupsError);
      showNotification(
        "error",
        `Erro ao carregar grupos do tipo: ${
          typeGroupsError.response?.data?.message || typeGroupsError.message
        }`,
      );
      hideLoader();
    }
  }, [
    typeGroupsData,
    isLoadingTypeGroups,
    isErrorTypeGroups,
    typeGroupsError,
    setFormData,
    hideLoader,
    showNotification,
    typeSelect.options,
    id,
  ]);

  // Loader para carregamento da query
  useEffect(() => {
    if (isLoadingTypeGroups) {
      showLoader();
    } else {
      hideLoader();
    }
  }, [isLoadingTypeGroups, showLoader, hideLoader]);

  // Mutação para associar grupos ao tipo
  const getOptions = useCallback(
    (fieldId) => selects[fieldId]?.options || [],
    [selects],
  );
  const getSelectedValue = useCallback(
    (fieldId) => selects[fieldId]?.selectedValue || null,
    [selects],
  );

  const handleBack = useCallback(() => {
    navigate("/almoxarifado/produtos/tipos");
  }, [navigate]);

  // Mensagem de estado via location
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  const isFormLoading = isLoadingTypeGroups;

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Grupos"
        showBackButton={true}
        backUrl="/almoxarifado/produtos/tipos"
      />

      <div className="container-fluid p-1">
        <Form
          initialFormData={formData}
          className="p-3 mt-2 rounded shadow-sm mb-2"
          showButtons={false}
          handleBack={handleBack}
          isLoading={isFormLoading}
        >
          {() => {
            const renderedSections = new Set();

            return associeteGroupFields.map((section, index) => {
              const sectionType = section.section.split(" - ")[0];
              const showHeader = !renderedSections.has(sectionType);

              if (showHeader) {
                renderedSections.add(sectionType);
              }

              return (
                <div key={section.section + " " + index}>
                  {showHeader && (
                    <SectionHeader
                      section={section.section}
                      viewTable={false}
                      setViewTable={() => {}}
                      addFieldsInData={() => {}}
                    />
                  )}

                  <SimpleBody
                    fields={section.fields}
                    formErrors={normalizeFormErrors(formErrors)}
                    formData={formData}
                    handleFieldChange={handleChange}
                    getOptions={getOptions}
                    getSelectedValue={getSelectedValue}
                    getSelectMeta={getSelectMeta}
                  />

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "start",
                      flexWrap: "wrap",
                      mt: 2,
                    }}
                  >
                    {typeGroupsData?.length > 0 &&
                      typeGroupsData?.map((group) => (
                        <Chip
                          key={group.id}
                          label={group.name}
                          sx={{ margin: "2px" }}
                        />
                      ))}
                  </Box>
                </div>
              );
            });
          }}
        </Form>
      </div>
    </MainLayout>
  );
};

export default AttachGroupToTypePage;
